import "./App.scss";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Navbar from "./components/layout/Navbar/Navbar";
import Sidebar from "./components/layout/Sidebar/Sidebar";
import { useState, useEffect, useCallback } from "react";
import { initializeApi, getApiInstance } from "./utils/api";
import Login from "./pages/login/Login";
import { useTranslation } from "react-i18next";

import Components from "./pages/components/Components";
import Dashboard from "./pages/dashboard/Dashboard";
import Comunidades from "./pages/administracion/comunidades/Comunidades";
import Intermediarios from "./pages/administracion/intermediarios/Intermediarios";
import Paises from "./pages/administracion/paises/Paises";
import Productores from "./pages/administracion/productores/Productores";
import Provincias from "./pages/administracion/provincias/Provincias";
import Usuarios from "./pages/administracion/usuarios/Usuarios";
import CentrosProductores from "./pages/centrosProductores/CentrosProductores";
import Gestores from "./pages/gestores/Gestores";
import Residuos from "./pages/residuos/Residuos";
import CantidadesGeneradas from "./pages/informes/cantidadesGeneradas/CantidadesGeneradas";
import Costes from "./pages/informes/costes/Costes";
import HuellaCarbono from "./pages/informes/huellaCarbono/HuellaCarbono";
import SegregacionValorizacion from "./pages/informes/segregacionValorizacion/SegregacionValorizacion";
import ObrasMenores from "./pages/obrasMenores/ObrasMenores";
import { ZoneProvider } from "./context/ZoneContext";
import FichaGestor from "./pages/fichaGestor/FichaGestor";
import FichaCentroProductor from "./pages/fichaCentroProductor/FichaCentroProductor";
import FichaObraMenor from "./pages/fichaObraMenor/FichaObraMenor";
import PerfilUsuario from "./pages/perfilUsuario/perfilUsuario";
import FichaProductor from "./pages/administracion/fichaProductor/FichaProductor";
import { SearchProvider } from "./context/SearchContext";
import TitleManager from "./TitleManager";
import ForgotPassword from "./pages/forgotPassword/ForgotPassword";
import ResetPassword from "./pages/resetPassword/ResetPassword";

function App() {
    const { t } = useTranslation();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem("token"));

    const logoutUser = useCallback(() => {
        localStorage.removeItem("token");
        setIsAuthenticated(false);
        setUserRole(null);
        setUser(null);
    }, []);

    useEffect(() => {
        const initialize = async () => {
            await initializeApi(logoutUser);
            if (token) {
                const decodedToken = JSON.parse(atob(token.split(".")[1]));

                // Verificar si el token ha expirado
                const currentTime = Math.floor(Date.now() / 1000);
                if (decodedToken.exp && decodedToken.exp < currentTime) {
                    logoutUser();
                    return;
                }

                setIsAuthenticated(true);
                setUserRole(
                    decodedToken[
                        "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                    ]
                );

                // Extraer información del usuario del token
                setUser({
                    name:
                        decodedToken[
                            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"
                        ] || "Usuario",
                    email:
                        decodedToken[
                            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
                        ] || "usuario@empresa.com",
                    role:
                        decodedToken[
                            "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                        ] || "Usuario",
                    codAcceso: decodedToken["CodAcceso"] || "2",
                });
            }
            setIsInitializing(false);
        };
        initialize();
    }, [logoutUser, token]);

    const handleToggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleToggleCollapse = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
    };

    const authenticateUser = (newToken) => {
        localStorage.setItem("token", newToken);
        const decodedToken = JSON.parse(atob(newToken.split(".")[1]));
        setIsAuthenticated(true);
        setUserRole(
            decodedToken[
                "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
            ]
        );

        // Extraer información del usuario del token
        setUser({
            name:
                decodedToken[
                    "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"
                ] || "Usuario",
            email:
                decodedToken[
                    "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
                ] || "usuario@empresa.com",
            role:
                decodedToken[
                    "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                ] || "Usuario",
        });

        setToken(newToken);
    };

    return (
        <BrowserRouter>
            <div className="App">
                {!isAuthenticated ? (
                    <>
                        <TitleManager />
                        <Routes>
                            <Route
                                path="/*"
                                element={<Navigate to="/login" />}
                            />
                            <Route
                                path="/login"
                                element={<Login onLogin={authenticateUser} />}
                            />
                            <Route
                                path="/forgot-password"
                                element={<ForgotPassword />}
                            />
                            <Route
                                path="/reset-password"
                                element={<ResetPassword />}
                            />
                        </Routes>
                    </>
                ) : (
                    <ZoneProvider>
                        <SearchProvider>
                            <Navbar
                                onToggleSidebar={handleToggleSidebar}
                                isSidebarOpen={isSidebarOpen}
                                isSidebarCollapsed={isSidebarCollapsed}
                                user={user}
                                onLogout={logoutUser}
                            />

                            <div className="app__container">
                                <Sidebar
                                    isOpen={isSidebarOpen}
                                    isCollapsed={isSidebarCollapsed}
                                    onToggleCollapse={handleToggleCollapse}
                                    onClose={() => setIsSidebarOpen(false)}
                                    userRole={userRole}
                                />

                                <main
                                    className={`app__content ${
                                        isSidebarCollapsed
                                            ? "app__content--collapsed"
                                            : ""
                                    }`}
                                >
                                    <div className="app__content-wrapper">
                                        <Routes>
                                            <Route
                                                path="/"
                                                element={
                                                    <Navigate
                                                        to="/dashboard"
                                                        replace
                                                    />
                                                }
                                            />

                                            <Route
                                                path="/login"
                                                element={<Navigate to="/" />}
                                            />

                                            {/* Dashboard */}
                                            <Route
                                                path="/dashboard"
                                                element={<Dashboard />}
                                            />

                                            {/* Perfil de Usuario */}
                                            <Route
                                                path="/perfil-usuario"
                                                element={
                                                    <PerfilUsuario
                                                        user={user}
                                                    />
                                                }
                                            />

                                            {/* Administración */}
                                            <Route
                                                path="/administracion/comunidades"
                                                element={
                                                    <Comunidades user={user} />
                                                }
                                            />
                                            <Route
                                                path="/administracion/intermediarios"
                                                element={
                                                    <Intermediarios
                                                        user={user}
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/administracion/paises"
                                                element={<Paises user={user} />}
                                            />
                                            <Route
                                                path="/administracion/productores"
                                                element={
                                                    <Productores user={user} />
                                                }
                                            />
                                            <Route
                                                path="/administracion/productores/:id"
                                                element={
                                                    <FichaProductor
                                                        user={user}
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/administracion/provincias"
                                                element={
                                                    <Provincias user={user} />
                                                }
                                            />
                                            <Route
                                                path="/administracion/usuarios"
                                                element={
                                                    <Usuarios user={user} />
                                                }
                                            />

                                            {/* Otrass */}
                                            <Route
                                                path="/centros-productores"
                                                element={
                                                    <CentrosProductores
                                                        user={user}
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/gestores"
                                                element={
                                                    <Gestores user={user} />
                                                }
                                            />
                                            <Route
                                                path="/gestores/:id"
                                                element={
                                                    <FichaGestor user={user} />
                                                }
                                            />
                                            <Route
                                                path="/centros-productores/:id"
                                                element={
                                                    <FichaCentroProductor
                                                        user={user}
                                                    />
                                                }
                                            />
                                            {/* <Route
                                                path="/obras-menores"
                                                element={<ObrasMenores user={user} />}
                                            />
                                            <Route
                                                path="/obras-menores/:id"
                                                element={<FichaObraMenor user={user} />}
                                            /> */}
                                            <Route
                                                path="/residuos"
                                                element={
                                                    <Residuos user={user} />
                                                }
                                            />

                                            {/*  Informes */}
                                            <Route
                                                path="/informes/cantidades-generadas"
                                                element={
                                                    <CantidadesGeneradas />
                                                }
                                            />
                                            <Route
                                                path="/informes/costes"
                                                element={<Costes />}
                                            />
                                            {/* <Route
                                                path="/informes/huella-carbono"
                                                element={<HuellaCarbono />}
                                            /> */}
                                            <Route
                                                path="/informes/segregacion-valorizacion"
                                                element={
                                                    <SegregacionValorizacion />
                                                }
                                            />

                                            {/* tests */}
                                            <Route
                                                path="/components"
                                                element={<Components />}
                                            />
                                            <Route
                                                path="*"
                                                element={
                                                    <Navigate
                                                        to="/dashboard"
                                                        replace
                                                    />
                                                }
                                            />
                                        </Routes>
                                    </div>
                                </main>
                            </div>
                        </SearchProvider>
                    </ZoneProvider>
                )}
            </div>
        </BrowserRouter>
    );
}

export default App;
